import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import enGB from 'date-fns/locale/en-GB'
import enDE from 'date-fns/locale/de'
import { Chart } from 'react-chartjs-2'
import { makeStyles } from 'tss-react/mui'
import { isEmpty } from 'lodash'
import {
  eachDayOfInterval,
  eachMonthOfInterval,
  eachQuarterOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  format,
} from 'date-fns'
import { groupBy } from '../../shared/utils'
import {
  DAY_PATTERN,
  GapExtended,
  GapItem,
  MONTH_PATTERN,
  QUARTER_PATTERN,
  WEEK_PATTERN,
  YEAR_PATTERN,
} from '../../shared/model/models'
import { gapAPI } from '../../shared/api/GapAPI'
import { DiversityDimenstionConfig, ModuleConfig } from '../../shared/model'
import { fsColors } from '../../shared/constants'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { setContextVariantGaps } from '../../store/actions'
import FsAlert, { FsAlertRef } from '../common/atoms/Alert'

type FsGapHistoryProps = {
  gap: GapExtended
  contextModelVariantId: number
  moduleConfig?: ModuleConfig
  diversityDimenstionConfig?: DiversityDimenstionConfig
}

const getOptions = (
  gapItems: GapItem[],
  key: keyof GapItem & string,
  p: { start: Date | undefined; end: Date | undefined },
  t: any,
  defaultLocale: Locale,
  diversityDimenstionConfig?: DiversityDimenstionConfig,
) => {
  const localeGapItems = gapItems.map((gi) => ({
    ...gi,
    timeOfImportDay: format(
      new Date(gi.timeOfImport),
      defaultLocale === enDE ? 'dd.MM.yyyy' : DAY_PATTERN,
      {
        locale: defaultLocale,
      },
    ),
    timeOfImportMonth: format(new Date(gi.timeOfImport), MONTH_PATTERN, {
      locale: defaultLocale,
    }),
  }))
  const groupedMap = groupBy(localeGapItems, key)
  const rawSeriesData: GapItem[] = []
  const seriesData: (GapItem | undefined)[] = []

  if (p.start === undefined || p.end === undefined || p.start > p.end) {
    return null
  }

  groupedMap.forEach((gaps: GapItem[]) => {
    const lastItem = gaps[gaps.length - 1]
    if (lastItem) {
      rawSeriesData.push(lastItem)
    }
  })

  let axis: string[]
  switch (key) {
    case 'timeOfImportDay':
      axis = eachDayOfInterval({
        start: p.start,
        end: p.end,
      }).map((it) =>
        format(it, defaultLocale === enDE ? 'dd.MM.yyyy' : DAY_PATTERN, { locale: defaultLocale }),
      )
      break
    case 'timeOfImportWeek':
      axis = eachWeekOfInterval(
        {
          start: p.start,
          end: p.end,
        },
        { locale: defaultLocale },
      ).map((it) => format(it, WEEK_PATTERN, { locale: defaultLocale }))
      break
    case 'timeOfImportMonth':
      axis = eachMonthOfInterval({
        start: p.start,
        end: p.end,
      }).map((it) => format(it, MONTH_PATTERN, { locale: defaultLocale }))
      break
    case 'timeOfImportQuarter':
      axis = eachQuarterOfInterval({
        start: p.start,
        end: p.end,
      }).map((it) => format(it, QUARTER_PATTERN, { locale: defaultLocale }))
      break
    case 'timeOfImportYear':
      axis = eachYearOfInterval({
        start: p.start,
        end: p.end,
      }).map((it) => format(it, YEAR_PATTERN, { locale: defaultLocale }))
      break
    default:
      axis = Array.from(groupedMap.keys()) as string[]
  }
  const firstDate = Array.from(groupedMap.keys())[0] as string
  axis = axis.slice(axis.indexOf(firstDate))
  let gapItem: GapItem
  axis.forEach((value) => {
    gapItem = rawSeriesData.find((e) => e[`${key}`] === value) || gapItem
    seriesData.push(gapItem)
  })

  return {
    color: diversityDimenstionConfig?.fsColor
      ? [diversityDimenstionConfig?.fsColor.light, fsColors.fsGray.colors[1]]
      : ['green', 'blue'],
    tooltip: {
      trigger: 'axis',
    },
    legend: {
      data: [t('adjusted'), t('unadjusted')],
      right: 'right',
    },
    grid: {
      left: '5%',
      right: '5%',
      bottom: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: axis,
      boundaryGap: true,
      axisLabel: {
        show: true,
      },
      axisTick: {
        alignWithLabel: true,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} %',
      },
    },
    series: [
      {
        name: t('adjusted'),
        data: seriesData.map((it: GapItem | undefined) =>
          it && it.adjusted ? Number(it.adjusted).toFixed(1) : undefined,
        ),
        type: 'line',
        lineStyle: { width: 3 },
        smooth: true,
        clip: false,
      },
      {
        name: t('unadjusted'),
        data: seriesData.map((it: GapItem | undefined) =>
          it ? Number(it.unadjusted).toFixed(1) : undefined,
        ),
        type: 'line',
        lineStyle: { width: 3 },
        smooth: true,
        clip: false,
      },
    ],
  }
}

const useStyles = makeStyles<string | undefined>()(() => ({
  tabs: {
    '& .MuiButtonBase-root.MuiTab-root': {
      textTransform: 'capitalize',
      fontSize: '1rem',
    },
  },
}))

const FsGapHistory = (props: FsGapHistoryProps) => {
  const { contextModelVariantId, moduleConfig, diversityDimenstionConfig, gap } = props
  const { t, i18n } = useTranslation()
  const { contextVariantGaps, runs } = useAppSelector((state) => state.currentAnalysis)
  const [gaps, setGaps] = useState<GapItem[]>([])
  const theme = useTheme()
  const currentDate = new Date()
  const dispatch = useAppDispatch()
  const alertRef = useRef<FsAlertRef>(null)

  const defaultLocale = i18n.language === 'en' ? enGB : enDE

  const [startValue, setStartValue] = useState<Date | undefined>(undefined)
  const [endValue, setEndValue] = useState<Date | undefined>(undefined)

  const [firstRunTime, setFirstRunTime] = useState<Date | undefined>(undefined)
  const [lastRunTime, setLastRunTime] = useState<Date | undefined>(undefined)

  const updateStartDate = (startDate: Date): void => {
    setStartValue(startDate)
  }
  const updateEndDate = (endDate: Date): void => {
    setEndValue(endDate)
  }

  const tabViews: [string, keyof GapItem][] = [
    ['daily', 'timeOfImportDay'],
    ['weekly', 'timeOfImportWeek'],
    ['monthly', 'timeOfImportMonth'],
    ['quarterly', 'timeOfImportQuarter'],
    ['yearly', 'timeOfImportYear'],
  ]
  const [tabIndex, setTabIndex] = useState(tabViews[2][0])

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabIndex(newValue)
  }

  const updateGapsData = (start: Date, end: Date) => {
    gapAPI
      .getGaps(contextModelVariantId, start, end)
      .then((items: GapItem[]) => {
        const gapItems = [...items].sort((thisGap, otherGap) =>
          thisGap.timeOfImport < otherGap.timeOfImport ? -1 : 1,
        )
        setGaps(gapItems)
      })
      .catch((error) => {
        alertRef.current?.openAlert({
          severity: 'error',
          message: t('something.went.wrong'),
        })
      })
  }

  useEffect(() => {
    if (runs) {
      const firstRun = runs[0]
      const lastRun = runs[runs.length - 1]

      setStartValue(new Date(firstRun.runAt))
      setEndValue(currentDate)

      setFirstRunTime(firstRun.runAt)
      setLastRunTime(lastRun.runAt)
      if (contextVariantGaps && !isEmpty(contextVariantGaps[contextModelVariantId])) {
        setGaps(contextVariantGaps[contextModelVariantId])
      } else {
        dispatch(
          setContextVariantGaps({
            contextVariantId: contextModelVariantId,
            startDate: new Date(firstRun.runAt),
            endDate: currentDate,
          }),
        )
      }
    }
  }, [contextModelVariantId, runs, contextVariantGaps])

  const { classes } = useStyles(diversityDimenstionConfig?.fsColor.light)

  return (
    <Box className="history_walkthrough">
      <FsAlert ref={alertRef} />
      <Box>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={defaultLocale}>
          <Stack
            mt={2}
            mb={2}
            spacing={2}
            direction="row"
            p={1}
            justifyContent="flex-start"
            alignItems="center"
          >
            <DatePicker
              disableFuture
              label={t('start.date')}
              views={['year', 'month', 'day']}
              value={startValue}
              minDate={firstRunTime}
              maxDate={
                lastRunTime !== undefined && endValue !== undefined && endValue > lastRunTime
                  ? lastRunTime
                  : endValue
              }
              onAccept={(newValue: any) => {
                if (newValue && endValue) {
                  updateStartDate!(newValue)
                  setStartValue(newValue)
                  updateGapsData(newValue, endValue)
                }
              }}
              slotProps={{
                field: {
                  readOnly: true,
                },
              }}
            />
            <DatePicker
              disableFuture
              label={t('end.date')}
              views={['year', 'month', 'day']}
              minDate={
                firstRunTime !== undefined && startValue !== undefined && startValue < firstRunTime
                  ? firstRunTime
                  : startValue
              }
              maxDate={currentDate}
              value={endValue}
              onAccept={(newValue: any) => {
                if (newValue && startValue) {
                  updateEndDate!(newValue)
                  setEndValue(newValue)
                  updateGapsData(startValue, newValue)
                }
              }}
              slotProps={{
                field: {
                  readOnly: true,
                },
              }}
            />
          </Stack>
        </LocalizationProvider>
      </Box>
      {gaps.length > 0 ? (
        <Box sx={{ width: '100%' }}>
          <Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabIndex}
                onChange={handleChange}
                variant="scrollable"
                className={classes.tabs}
                aria-label="scrollable tabs"
              >
                {tabViews.map((tabView) => (
                  <Tab
                    label={t(tabView[0] as any)}
                    value={tabView[0]}
                    key={tabView[0]}
                    sx={{ fontWeight: 600 }}
                  />
                ))}
              </Tabs>
            </Box>
            {tabViews.map((tabView) => {
              const options = getOptions(
                gaps,
                tabView[1],
                {
                  start: startValue,
                  end: endValue,
                },
                t,
                defaultLocale,
                diversityDimenstionConfig,
              )
              return (
                tabIndex === tabView[0] && (
                  <Box key={tabView[0]} mt="16px">
                    <Box style={{ height: '470px' }}>
                      <Chart
                        type="line"
                        id={`history_chart_${tabView[0]}`}
                        data={{
                          labels: options?.xAxis.data,
                          datasets: [
                            {
                              data: options?.series[0].data,
                              borderColor: options?.color[0],
                              label: options?.legend.data[0],
                              borderWidth: 2,
                            },
                            {
                              data: options?.series[1].data,
                              borderColor: options?.color[1],
                              label: options?.legend.data[1],
                              borderWidth: 2,
                            },
                          ],
                        }}
                        options={{
                          maintainAspectRatio: false,
                          locale: i18n.language,
                          elements: {
                            point: {
                              radius: 4,
                            },
                          },
                          scales: {
                            x: {
                              offset: true,
                              ticks: {
                                autoSkip: true,
                                maxTicksLimit: 20,
                                color: theme.palette.text.primary,
                                font: (context) => {
                                  const { width } = context.chart
                                  const size = Math.round(width / 32)
                                  return {
                                    size: size < 14 ? size : 14,
                                  }
                                },
                              },
                              grid: {
                                color: theme.palette.divider,
                              },
                              border: {
                                color: theme.palette.text.disabled,
                              },
                            },
                            y: {
                              ticks: {
                                color: theme.palette.text.primary,
                                callback: (value) =>
                                  `${Number(value).toLocaleString(i18n.language, {
                                    maximumFractionDigits: 1,
                                    minimumFractionDigits: 1,
                                  })} %`,
                                font: (context) => {
                                  const { width } = context.chart
                                  const size = Math.round(width / 32)
                                  return {
                                    size: size < 14 ? size : 14,
                                  }
                                },
                                stepSize: 1,
                              },
                              grid: {
                                color: theme.palette.divider,
                              },
                              border: {
                                color: theme.palette.text.disabled,
                              },
                            },
                          },
                          plugins: {
                            legend: {
                              position: 'top',
                              align: 'end',
                              labels: {
                                usePointStyle: true,
                                pointStyle: 'line',
                              },
                              onHover: (event) => {
                                if (event.native?.target)
                                  // @ts-ignore
                                  event.native.target.style.cursor = 'pointer'
                              },
                              onLeave: (event) => {
                                if (event.native?.target)
                                  // @ts-ignore
                                  event.native.target.style.cursor = 'default'
                              },
                            },
                            tooltip: {
                              callbacks: {
                                label: (context) =>
                                  `${context.dataset.label}: ${context.formattedValue} %`,
                              },
                            },
                            datalabels: {
                              display: false,
                            },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                )
              )
            })}
          </Box>
        </Box>
      ) : (
        <Typography variant="h3">{t('no.history.data')}</Typography>
      )}
    </Box>
  )
}

export default FsGapHistory
